import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import Modal from 'react-modal';

import { BaseReact } from '../../base.model';
import { ProfileQL } from '../../models/Profile';
import { CancelResponse, ToBillArguments, ToBillEvent } from '../../models/Factura';
import { CfdiIndexEntry, CfdiIndexFilter, CfdiIndexPage, compactFilter, filterFromSearch, filterToSearchParams } from '../../models/CfdiIndex';
import { graphqlSchema } from '../../services/graphql.schema';
import { getCfdiCancelStatus } from '../../services/fiscalpopApi';
import sharedToasterSubject from '../../services/shared.toasterSubject';
import { clearGqlTypename } from '../../utils/formatting';
import SectionTitleBar from '../components/titlebar';
import Pagination from '../Forms/Pagination';
import Button from '../Forms/Button';
import Loading from '../Animations/loadScreen';
import FacturasFilters from './Facturas.filters';
import FacturasRow, { relatedWarning } from './Facturas.row';
import FacturasGroup from './Dialogs/Facturas.group';
import { resolveEditRoute } from './facturas.actions';

import './Facturas.scss';

interface FacturasProps extends BaseReact {
    currentUser: ProfileQL;
}

const PAGE_SIZE = 25;

const modalStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        maxHeight: '90vh',
        padding: '6px',
        background: 'transparent',
        borderRadius: '10px',
        overflow: 'visible',
        border: 'none',
        boxShadow: 'none',
    }
};

type CancelStatus = '' | 'cancelable sin aceptación' | 'cancelable con aceptación' | 'no cancelable';

const readPage = (params: URLSearchParams) => {
    const page = parseInt(params.get('page') || '1', 10);
    return isNaN(page) || page < 1 ? 1 : page;
};

function Facturas({ currentUser, history, location }: FacturasProps) {
    const authToken = currentUser.professionalProfile.authToken;

    // ── URL-backed state (filter + page) ──────────────────────────────────────
    const initial = useMemo(() => filterFromSearch(location.search), []); // eslint-disable-line react-hooks/exhaustive-deps
    const [filter, setFilter] = useState<CfdiIndexFilter>(compactFilter(initial.filter));
    const [page, setPage] = useState<number>(readPage(initial.extra));

    useEffect(() => {
        const search = filterToSearchParams(filter, { page: page > 1 ? page : undefined });
        if (search !== location.search) {
            history.replace({ pathname: location.pathname, search });
        }
    }, [filter, page]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Data ──────────────────────────────────────────────────────────────────
    const { data, loading, error, refetch } = useQuery<{ getCfdiIndexPage: CfdiIndexPage }>(graphqlSchema.FISCALPOP.CFDI.getCfdiIndexPage, {
        variables: { filter, page, pageSize: PAGE_SIZE },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });
    const pageData = data?.getCfdiIndexPage;
    const items = pageData?.items || [];
    const total = pageData?.total || 0;
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    useEffect(() => {
        if (error) {
            console.error('getCfdiIndexPage error: ', error);
            sharedToasterSubject.next({ type: 'error', message: `Error al cargar facturas: ${error.graphQLErrors?.[0]?.message || error.message}` });
        }
    }, [error]);

    useEffect(() => {
        // Clamp page if filters shrink the result set
        if (pageData && page > pages) setPage(pages);
    }, [pageData, page, pages]);

    // ── UI state ──────────────────────────────────────────────────────────────
    const [unsetTransform, setUnsetTransform] = useState(false);
    const [groupTarget, setGroupTarget] = useState<CfdiIndexEntry>(null);
    const [cancelTarget, setCancelTarget] = useState<CfdiIndexEntry>(null);
    const [editTarget, setEditTarget] = useState<CfdiIndexEntry>(null);
    const [cancelStatus, setCancelStatus] = useState<CancelStatus>('');
    const [busyUuid, setBusyUuid] = useState('');
    const [isResolvingEdit, setIsResolvingEdit] = useState(false);
    const [pendingArgumentUse, setPendingArgumentUse] = useState<'cartaporte' | null>(null);

    // ── Filter / page handlers ────────────────────────────────────────────────
    const handleFilterChange = useCallback((next: CfdiIndexFilter) => {
        setFilter(compactFilter(next));
        setPage(1);
    }, []);

    const handleFilterUuid = useCallback((uuid: string) => {
        setFilter({ uuid });
        setPage(1);
    }, []);

    // ── Cancel flow ───────────────────────────────────────────────────────────
    const [cancelBill] = useMutation(graphqlSchema.FISCALPOP.CFDI.setBillToCancel, {
        onCompleted: ({ setBillToCancel }: { setBillToCancel: CancelResponse }) => {
            console.log('Cancel Bill Response: ', setBillToCancel);
            setBusyUuid('');
            sharedToasterSubject.next({ type: 'confirm', message: 'Se canceló la factura' });
            refetch();
        },
        onError: (e) => {
            console.error('Error canceling bill: ', e);
            setBusyUuid('');
            sharedToasterSubject.next({ type: 'error', message: `Error: ${e.graphQLErrors?.[0]?.message || 'no se pudo cancelar la factura'}` });
        }
    });

    useEffect(() => {
        const target = cancelTarget || editTarget;
        if (!target) return;
        setCancelStatus('');
        getCfdiCancelStatus(authToken, target.uuid)
            .then((statusCFDI) => setCancelStatus((statusCFDI.toCancelProcess || '').toLowerCase() as CancelStatus))
            .catch((e) => {
                console.error('ERROR GETTING CFDI STATUS: ', e);
                setCancelStatus('cancelable con aceptación');
            });
    }, [cancelTarget, editTarget, authToken]);

    const confirmCancel = () => {
        if (!cancelTarget || busyUuid) return;
        setBusyUuid(cancelTarget.uuid);
        sharedToasterSubject.next({ type: 'warning', message: 'Cancelando factura...' });
        cancelBill({ variables: { uuid: cancelTarget.uuid, billArgumentId: cancelTarget.argumentId } });
        setCancelTarget(null);
    };

    // ── Edit flow ─────────────────────────────────────────────────────────────
    const [fetchArgument] = useLazyQuery(graphqlSchema.FISCALPOP.CFDI.getBillArgumentById, {
        fetchPolicy: 'network-only',
        onCompleted: ({ getBillArgumentById }: { getBillArgumentById: ToBillArguments<ToBillEvent> }) => {
            setIsResolvingEdit(false);
            const argument: ToBillArguments<ToBillEvent> = clearGqlTypename(getBillArgumentById);
            if (pendingArgumentUse === 'cartaporte') {
                setPendingArgumentUse(null);
                history.push({ pathname: `/facturacion/cartaporte/preview/${argument._id}`, state: { billArgument: argument } });
                return;
            }
            const uuid = editTarget?.uuid;
            setEditTarget(null);
            const route = resolveEditRoute(argument, uuid);
            if (!route) {
                sharedToasterSubject.next({ type: 'error', message: 'Esta factura ya no forma parte de una relación editable (posiblemente fue sustituida)' });
                return;
            }
            history.push(route);
        },
        onError: (e) => {
            setIsResolvingEdit(false);
            setPendingArgumentUse(null);
            sharedToasterSubject.next({ type: 'error', message: `No fue posible cargar la factura: ${e.graphQLErrors?.[0]?.message || e.message}` });
        }
    });

    const confirmEdit = () => {
        if (!editTarget || isResolvingEdit) return;
        setIsResolvingEdit(true);
        fetchArgument({ variables: { argumentId: editTarget.argumentId } });
    };

    const openCartaPorte = (entry: CfdiIndexEntry) => {
        if (!entry.argumentId) return;
        setPendingArgumentUse('cartaporte');
        fetchArgument({ variables: { argumentId: entry.argumentId } });
    };

    const goToReports = () => {
        history.push({ pathname: '/facturas/reportes', search: filterToSearchParams(filter) });
    };

    const blockedByStatus = !cancelStatus || cancelStatus === 'no cancelable';
    const statusButtonLabel = (verb: string) => !cancelStatus ? 'Revisando status de CFDI' : (cancelStatus === 'no cancelable' ? 'CFDI No Cancelable' : `Sí, ${verb} factura`);

    return (
        <div id="Facturas">
            <SectionTitleBar currentUser={currentUser} title="Facturas emitidas" />
            <div className="facturasContent">
                <FacturasFilters filter={filter} isLoading={loading} onChange={handleFilterChange} />
                <div className="card table">
                    <div className="cardTitle _withActions">
                        <h4>
                            {total ? `${total.toLocaleString('es-MX')} CFDI${total === 1 ? '' : 's'}` : (loading ? 'Cargando…' : 'Sin resultados')}
                            {total ? <span className="_pageInfo"> · página {page} de {pages}</span> : null}
                        </h4>
                        <Button secondary={true} size="small" handleClick={goToReports}>
                            <span>Descargar reporte</span>
                        </Button>
                    </div>
                    <div className="cardBody" id="FacturasBody">
                        <Loading display={loading && !items.length} relativePos={true} />
                        <div className={`_entriesWrapper ${unsetTransform ? 'noTransform' : ''} ${loading ? '_refreshing' : ''}`}>
                            {
                                items.map(entry => (
                                    <FacturasRow
                                        key={entry.uuid}
                                        entry={entry}
                                        authToken={authToken}
                                        busyUuid={busyUuid}
                                        setUnsetTransform={setUnsetTransform}
                                        onOpenGroup={setGroupTarget}
                                        onAskCancel={setCancelTarget}
                                        onAskEdit={setEditTarget}
                                        onCartaPortePreview={openCartaPorte}
                                        onFilterUuid={handleFilterUuid}
                                    />
                                ))
                            }
                            {
                                !loading && !items.length ?
                                    <div className="_emptyState">
                                        <i className="material-icons">search_off</i>
                                        <p>No se encontraron CFDIs con los filtros seleccionados.</p>
                                        <p className="_hint">Si acabas de activar esta vista y no ves facturas anteriores, es necesario ejecutar la migración del índice de CFDIs.</p>
                                    </div>
                                    : null
                            }
                        </div>
                        <Pagination page={page} pages={pages} onPage={setPage} />
                    </div>
                </div>
            </div>

            <Modal isOpen={!!groupTarget} onRequestClose={() => setGroupTarget(null)} style={modalStyles} ariaHideApp={false} contentLabel="Relación entre facturas">
                {
                    groupTarget ?
                        <FacturasGroup
                            argumentId={groupTarget.argumentId}
                            selectedUuid={groupTarget.uuid}
                            authToken={authToken}
                            busyUuid={busyUuid}
                            requestClose={() => setGroupTarget(null)}
                            onAskCancel={(entry) => { setGroupTarget(null); setCancelTarget(entry); }}
                            onAskEdit={(entry) => { setGroupTarget(null); setEditTarget(entry); }}
                            onFilterUuid={handleFilterUuid}
                        />
                        : <div />
                }
            </Modal>

            <Modal isOpen={!!cancelTarget} onRequestClose={() => setCancelTarget(null)} style={modalStyles} ariaHideApp={false} contentLabel="Confirmar cancelar factura">
                <div className="card table" id="cancelModal">
                    <div className="cardTitle _red">
                        <h4>Confirma cancelar factura</h4>
                    </div>
                    <div className="cardBody">
                        <p>¿Estás seguro(a) que quieres cancelar la factura <br /> <b>{cancelTarget?.uuid || ''}</b>?</p>
                        <p>{cancelTarget ? `${cancelTarget.receptorNombre} · Serie ${cancelTarget.serie || '—'} Folio ${cancelTarget.folio || '—'}` : ''}</p>
                        {
                            cancelTarget && relatedWarning(cancelTarget) ?
                                <p className="_relatedWarning"><i className="material-icons">link</i> {relatedWarning(cancelTarget)}</p>
                                : null
                        }
                        <p>También puedes sustituirla si requieres corregirla en lugar de cancelarla.</p>
                        <div className="actions">
                            <Button secondary={true} handleClick={() => setCancelTarget(null)}>
                                <span>Mejor no</span>
                            </Button>
                            <Button primary={true} handleClick={confirmCancel} disabled={blockedByStatus}>
                                <span>{statusButtonLabel('cancelar')}</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal isOpen={!!editTarget} onRequestClose={() => setEditTarget(null)} style={modalStyles} ariaHideApp={false} contentLabel="Confirmar editar factura">
                <div className="card table" id="editModal">
                    <div className="cardTitle _yellow">
                        <h4>Confirma editar factura</h4>
                    </div>
                    <div className="cardBody">
                        <p>¿Estás seguro(a) que quieres editar la factura <br /> <b>{editTarget?.uuid || ''}</b>?</p>
                        {
                            editTarget && relatedWarning(editTarget) ?
                                <p className="_relatedWarning"><i className="material-icons">link</i> {relatedWarning(editTarget)}</p>
                                : null
                        }
                        <p>Al editar se cancela la factura actual y se emite una nueva que la sustituye. Solamente puede refacturarse si no hay alguna otra factura relacionada con esta, como complemento de pago o notas de crédito.</p>
                        <div className="actions">
                            <Button secondary={true} handleClick={() => setEditTarget(null)}>
                                <span>Mejor no</span>
                            </Button>
                            <Button primary={true} handleClick={confirmEdit} disabled={blockedByStatus || isResolvingEdit}>
                                <span>{isResolvingEdit ? 'Cargando factura…' : (!cancelStatus ? 'Revisando status de CFDI' : (cancelStatus === 'no cancelable' ? 'CFDI No Editable' : 'Sí, editar factura'))}</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default Facturas;
